import { HTMLAttributes, memo, useMemo } from 'react';
import styled from '@emotion/styled';
import { Button, message, Tooltip } from 'antd';
import useIM from 'hooks/business/im/useIM';
import VoucherOperation from './VoucherOperation';
import { useTranslate } from '@tolgee/react';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { VoucherCard } from 'components/VoucherCard';
import { useVoucherOperate } from 'hooks/business/useVoucherOperate';
import Empty from 'components/Empty';
import { useIsSupportSendVoucher, useSendVoucherDisabled } from 'hooks/business/chat/useChatSide';

const Right = styled.div`
  flex: 0 0 120px;
  display: flex;
  flex-direction: column;
  padding: 16px 10px 6px;
  justify-content: center;
  border-left: dashed 1px #e1e4eb;

  .ant-btn {
    width: 100%;
    margin-bottom: 10px;
  }

  .${({ theme }) => theme.getAntdPrefixedClassName('btn-text')} {
    padding: 0 5px;
  }
`;

const Operator = styled.div`
  margin-bottom: 22px;
  font-size: 12px;
  color: #84868c;
  word-break: break-all;
`;

export interface VoucherComponentProps extends HTMLAttributes<HTMLDivElement> {
  status: Conversation.Voucher.Vo['voucherStatus'];
  voucher: Conversation.Voucher.Vo;
  conversation: Conversation.Conversation;
  onDelete: (voucher: Conversation.Voucher.Vo) => void;
  onUpdate: (voucher: Conversation.Voucher.Vo) => void;
  onEdit: (voucher: Conversation.Voucher.Vo) => void;
  onCopyAndCreate: (voucher: Conversation.Voucher.Vo) => void;
}

function VoucherComponent(props: VoucherComponentProps) {
  const {
    status,
    voucher,
    conversation,
    onDelete,
    onUpdate,
    onEdit,
    onCopyAndCreate,
    ...otherProps
  } = props;

  const {
    externalVoucherId,
    voucherCode: code,
    suspendFlag,
    operatorNick,
    usageQuantity,
    currentUsage,
  } = voucher;

  const { canEdit, canCopy } = useVoucherOperate(voucher, conversation);
  const isTiktok = isTiKTokUpperCase(conversation.channel);

  const { t } = useTranslate();

  const supportSendVoucher = useIsSupportSendVoucher(conversation.channel);

  const { conversationId, controlStatus, channel, isAffiliateSip } = conversation;

  const { sendVoucher } = useIM();

  const { sendVoucherDisabled, sendDisabledTips } = useSendVoucherDisabled({
    channel,
    status,
    suspendFlag,
    isTiktok,
    currentUsage,
    usageQuantity,
    controlStatus,
    isAffiliateSip,
  });

  const voucherAfter = useMemo(() => {
    const chidlrens: React.ReactNode[] = [];
    if (status === 'ENDED') {
      if (operatorNick && channel === 'SHOPEE') {
        chidlrens.push(
          <Operator>
            <div>{t('coupon.field.operator')}:</div>
            <div>{operatorNick}</div>
          </Operator>,
        );
      }
      if (canCopy) {
        chidlrens.push(
          <Button size="middle" onClick={() => onCopyAndCreate(voucher)}>
            {t('coupon.action.recreate')}
          </Button>,
        );
      }
    } else {
      if (supportSendVoucher) {
        chidlrens.push(
          <Tooltip placement="left" title={sendDisabledTips}>
            <Button
              size="middle"
              type="primary"
              disabled={sendVoucherDisabled}
              onClick={() => {
                sendVoucher(conversationId, {
                  voucherId: externalVoucherId,
                  voucherCode: code,
                }).catch(() => {
                  message.error(t('chatWorkspace.couponPicker.toast.sendFailed'));
                });
              }}
            >
              {t('coupon.action.send')}
            </Button>
          </Tooltip>,
        );
      }
      if (canCopy) {
        chidlrens.push(
          <Button size="middle" onClick={() => onCopyAndCreate(voucher)}>
            {t('coupon.action.duplicate')}
          </Button>,
        );
      }
      if (canEdit) {
        chidlrens.push(
          <>
            <VoucherOperation
              status={status}
              conversation={conversation}
              voucher={voucher}
              onDelete={onDelete}
              onUpdate={onUpdate}
            />
            <Button size="middle" onClick={() => onEdit(voucher)}>
              {t('coupon.action.edit')}
            </Button>
          </>,
        );
      }
    }

    return <Right>{chidlrens.length > 0 ? chidlrens : <Empty />}</Right>;
  }, [
    canCopy,
    canEdit,
    channel,
    code,
    conversation,
    conversationId,
    externalVoucherId,
    onCopyAndCreate,
    onDelete,
    onEdit,
    onUpdate,
    operatorNick,
    sendDisabledTips,
    sendVoucher,
    sendVoucherDisabled,
    status,
    supportSendVoucher,
    t,
    voucher,
  ]);

  return (
    <VoucherCard
      {...otherProps}
      storeId={conversation.storeId}
      channel={conversation.channel}
      mask
      voucher={voucher}
      className="pl-3"
      after={voucherAfter}
    />
  );
}

export default memo(VoucherComponent);
